import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import {
  Target, TrendingUp, TrendingDown, Minus, ExternalLink, FileText,
  Printer, MessageSquareText, Phone, MapPin, Mail, Euro, BarChart3,
  Globe, Search, CheckCircle2
} from 'lucide-react';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const nf = (v) => Number(v ?? 0).toLocaleString('fr-FR');
const cf = (v) => Number(v ?? 0).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });
const fmtDate = (d) => {
  if (!d) return '';
  try { return new Date(d).toLocaleDateString('fr-FR'); } catch { return d; }
};

function ChangeBadge({ change }) {
  if (change === null || change === undefined) return <span className="text-neutral-400">—</span>;
  if (change > 0) return <span className="text-green-600 inline-flex items-center gap-1"><TrendingUp className="w-3.5 h-3.5" />+{change}</span>;
  if (change < 0) return <span className="text-red-500 inline-flex items-center gap-1"><TrendingDown className="w-3.5 h-3.5" />{change}</span>;
  return <span className="text-neutral-500 inline-flex items-center gap-1"><Minus className="w-3.5 h-3.5" />0</span>;
}

function Section({ icon: Icon, title, subtitle, children, testid }) {
  return (
    <section className="bg-white border border-neutral-200 rounded-lg mb-6 overflow-hidden" data-testid={testid}>
      <div className="p-4 border-b border-neutral-200 flex items-center gap-3">
        <div className="w-9 h-9 rounded bg-[#002FA7]/10 flex items-center justify-center flex-shrink-0">
          <Icon className="w-5 h-5 text-[#002FA7]" />
        </div>
        <div>
          <h2 className="font-heading font-semibold text-lg text-neutral-900 leading-tight">{title}</h2>
          {subtitle && <p className="text-sm text-neutral-500">{subtitle}</p>}
        </div>
      </div>
      <div className="p-4">{children}</div>
    </section>
  );
}

function StatBox({ label, value, accent }) {
  return (
    <div className="p-4 border border-neutral-200 rounded-lg bg-neutral-50">
      <p className="text-xs font-mono uppercase text-neutral-500 mb-1">{label}</p>
      <p className={`font-mono text-2xl font-bold ${accent || 'text-neutral-900'}`}>{value}</p>
    </div>
  );
}

export default function ClientView() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [report, setReport] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => { fetchAll(); }, [token]); // eslint-disable-line

  const fetchAll = async () => {
    try {
      const [cv, rd] = await Promise.all([
        axios.get(`${API}/public/client-view/${token}`),
        axios.get(`${API}/public/client-view/${token}/report-data`),
      ]);
      setData(cv.data);
      if (rd.data && rd.data.report) {
        setReport(rd.data);
        setSelectedId(rd.data.report.id);
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Lien invalide ou expiré');
    } finally {
      setLoading(false);
    }
  };

  const loadReport = async (reportId) => {
    if (reportId === selectedId) return;
    try {
      const rd = await axios.get(`${API}/public/client-view/${token}/report-data?report_id=${reportId}`);
      if (rd.data && rd.data.report) { setReport(rd.data); setSelectedId(reportId); window.scrollTo({ top: 0, behavior: 'smooth' }); }
    } catch (e) { /* ignore */ }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-neutral-300 border-t-[#002FA7] rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <ExternalLink className="w-8 h-8 text-red-500" />
          </div>
          <h1 className="font-heading font-bold text-2xl text-neutral-900 mb-2">Lien invalide</h1>
          <p className="text-neutral-500">{error}</p>
        </div>
      </div>
    );
  }

  const mgr = report?.manager_section || {};
  const tech = report?.technical_section || {};
  const gsc = report?.gsc_section || {};
  const ga = report?.ga_section || {};
  const sales = report?.sales_section;
  const sources = report?.lead_sources || [];
  const evoKws = report?.evolution_section?.keywords || [];
  const monthLabels = report?.evolution_section?.month_labels || [];
  const priorityKws = evoKws.filter(k => k.is_priority);
  const rep = report?.report || {};
  const periodLabel = (rep.period_start || rep.period_end)
    ? `${fmtDate(rep.period_start)} — ${fmtDate(rep.period_end)}`
    : (rep.period || '');
  const totalSourceLeads = sources.reduce((s, x) => s + x.count, 0);

  return (
    <div className="min-h-screen bg-neutral-50" data-testid="client-view-page">
      <style>{`@media print { .no-print { display:none !important; } body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }`}</style>

      {/* Header */}
      <header className="bg-white border-b border-neutral-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 bg-[#002FA7] rounded flex items-center justify-center flex-shrink-0">
              <span className="text-white font-heading font-bold text-sm">SEO</span>
            </div>
            <div className="min-w-0">
              <h1 className="font-heading font-bold text-lg text-neutral-900 truncate">{data?.client?.name}</h1>
              {data?.client?.website && (
                <a href={data.client.website.startsWith('http') ? data.client.website : `https://${data.client.website}`}
                   target="_blank" rel="noopener noreferrer"
                   className="text-sm text-[#002FA7] hover:underline flex items-center gap-1 truncate">
                  {data.client.website}<ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            {periodLabel && <span className="hidden sm:inline text-xs font-mono text-neutral-500 bg-neutral-100 px-2 py-1 rounded">{periodLabel}</span>}
            <Button onClick={() => window.print()} className="bg-[#002FA7] hover:bg-[#002277] no-print" data-testid="print-report-btn">
              <Printer className="w-4 h-4 mr-2" /> Télécharger / Imprimer
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        {rep.title && (
          <div className="mb-6">
            <p className="text-xs font-mono uppercase text-neutral-400">Rapport</p>
            <h2 className="font-heading font-bold text-2xl text-neutral-900">{rep.title}</h2>
            {periodLabel && <p className="text-sm text-neutral-500 font-mono">{periodLabel}</p>}
          </div>
        )}

        {!report && (
          <div className="bg-white border border-neutral-200 rounded-lg p-8 text-center text-neutral-500 mb-6">
            Aucun rapport publié pour le moment.
          </div>
        )}

        {report && (
          <>
            {/* 1. CONSULTANT ANALYSIS — FIRST */}
            <Section icon={MessageSquareText} title="Analyse du consultant" subtitle="Où ont été concentrés les efforts ce mois-ci" testid="section-consultant">
              {rep.summary && <p className="text-sm text-neutral-700 whitespace-pre-wrap mb-4">{rep.summary}</p>}
              {tech.consultant_notes ? (
                <div className="bg-neutral-50 border border-neutral-200 rounded p-4">
                  <p className="text-sm text-neutral-800 whitespace-pre-wrap leading-relaxed">{tech.consultant_notes}</p>
                </div>
              ) : (!rep.summary && <p className="text-sm text-neutral-400 italic">Aucune note du consultant pour cette période.</p>)}
              {tech.technical_actions?.length > 0 && (
                <div className="mt-4">
                  <h3 className="text-sm font-semibold text-neutral-700 mb-2">Actions réalisées</h3>
                  <ul className="space-y-1.5">
                    {tech.technical_actions.map((a, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-neutral-700">
                        <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                        <span>{typeof a === 'string' ? a : (a?.text || a?.action || JSON.stringify(a))}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </Section>

            {/* 2. CONTACTS (Formulaires + Appels) */}
            <Section icon={Mail} title="Contacts du mois" subtitle="Demandes reçues grâce au référencement" testid="section-contacts">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
                <StatBox label="Formulaires" value={nf(mgr.leads_this_month)} accent="text-[#002FA7]" />
                <StatBox label="Appels" value={nf(mgr.phone_calls)} accent="text-[#002FA7]" />
                <StatBox label="Itinéraires" value={nf(mgr.gbp_directions_auto)} />
                <StatBox label="Autres" value={nf(mgr.other_contacts)} />
                <StatBox label="Total contacts" value={nf(mgr.total_contacts)} accent="text-green-600" />
              </div>
              {(mgr.gbp_calls_auto > 0 || mgr.ga_calls_auto > 0) && (
                <p className="text-xs text-neutral-500 flex items-center gap-1 mb-4">
                  <Phone className="w-3 h-3" />
                  Détail appels : {nf(mgr.gbp_calls_auto)} via la fiche Google{mgr.ga_calls_auto > 0 && ` + ${nf(mgr.ga_calls_auto)} via le site web`}
                </p>
              )}

              {/* Form origins */}
              <div>
                <h3 className="text-sm font-semibold text-neutral-700 mb-2 flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-neutral-400" /> D'où viennent les formulaires
                </h3>
                {sources.length > 0 ? (
                  <div className="space-y-2">
                    {sources.map((s, i) => {
                      const pctVal = totalSourceLeads ? Math.round((s.count / totalSourceLeads) * 100) : 0;
                      return (
                        <div key={i} className="flex items-center gap-3" data-testid={`lead-source-${i}`}>
                          <div className="w-28 sm:w-44 text-sm text-neutral-700 truncate flex-shrink-0">{s.source}</div>
                          <div className="flex-1 h-5 bg-neutral-100 rounded overflow-hidden">
                            <div className="h-full bg-[#002FA7]/80" style={{ width: `${pctVal}%` }} />
                          </div>
                          <div className="w-20 text-right text-sm font-mono text-neutral-900 flex-shrink-0">{nf(s.count)} ({pctVal}%)</div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-neutral-400 italic">Origine des formulaires non disponible pour cette période.</p>
                )}
              </div>
            </Section>

            {/* 3. VENTES / CA / ROI */}
            <Section icon={Euro} title="Ventes, chiffre d'affaires & ROI" subtitle="Impact commercial du référencement" testid="section-sales">
              {sales && (sales.revenue > 0 || sales.wins > 0) ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <StatBox label="Ventes gagnées" value={nf(sales.wins)} accent="text-[#002FA7]" />
                  <StatBox label="Chiffre d'affaires" value={cf(sales.revenue)} accent="text-green-600" />
                  {sales.has_cost && <StatBox label="Coût marketing" value={cf(sales.cost)} />}
                  {sales.has_cost && sales.roi_percent !== null && (
                    <StatBox label="ROI" value={`${sales.roi_percent > 0 ? '+' : ''}${nf(sales.roi_percent)}%`} accent={sales.roi_percent >= 0 ? 'text-green-600' : 'text-red-500'} />
                  )}
                </div>
              ) : (
                <p className="text-sm text-neutral-400 italic">Aucune vente enregistrée sur la période.</p>
              )}
            </Section>

            {/* 4. TRAFFIC GSC / GA4 */}
            {(gsc?.totals || ga?.sessions !== undefined) && (
              <Section icon={BarChart3} title="Trafic & performances Google" subtitle="Search Console & Analytics" testid="section-traffic">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                  {gsc?.totals && <>
                    <StatBox label="Clics Google" value={nf(gsc.totals.clicks)} accent="text-[#002FA7]" />
                    <StatBox label="Impressions" value={nf(gsc.totals.impressions)} />
                    <StatBox label="CTR moyen" value={`${nf(gsc.totals.avg_ctr)}%`} />
                  </>}
                  {ga?.sessions !== undefined && <StatBox label="Sessions (GA4)" value={nf(ga.sessions)} />}
                </div>
                {gsc?.keywords?.length > 0 && (
                  <div className="overflow-x-auto">
                    <h3 className="text-sm font-semibold text-neutral-700 mb-2 flex items-center gap-2"><Search className="w-4 h-4 text-neutral-400" /> Top requêtes Google</h3>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-neutral-200 bg-neutral-50 text-left">
                          <th className="py-2 px-3 text-xs font-mono uppercase text-neutral-500">Requête</th>
                          <th className="py-2 px-3 text-right text-xs font-mono uppercase text-neutral-500">Clics</th>
                          <th className="py-2 px-3 text-right text-xs font-mono uppercase text-neutral-500">Impr.</th>
                          <th className="py-2 px-3 text-right text-xs font-mono uppercase text-neutral-500">CTR</th>
                          <th className="py-2 px-3 text-right text-xs font-mono uppercase text-neutral-500">Pos.</th>
                        </tr>
                      </thead>
                      <tbody>
                        {gsc.keywords.slice(0, 15).map((k, i) => (
                          <tr key={i} className="border-b border-neutral-100 last:border-0">
                            <td className="py-2 px-3 text-neutral-900">{k.keyword}</td>
                            <td className="py-2 px-3 text-right font-mono">{nf(k.clicks)}</td>
                            <td className="py-2 px-3 text-right font-mono text-neutral-500">{nf(k.impressions)}</td>
                            <td className="py-2 px-3 text-right font-mono">{k.ctr}%</td>
                            <td className="py-2 px-3 text-right font-mono font-bold">{k.position}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Section>
            )}

            {/* 5. KEYWORDS & POSITIONS */}
            <Section icon={Target} title="Mots-clés & positions" subtitle={`${nf(tech.total_keywords)} mots-clés suivis`} testid="section-keywords">
              <div className="grid grid-cols-3 gap-3 mb-5">
                <StatBox label="Suivis" value={nf(tech.total_keywords)} accent="text-[#002FA7]" />
                <StatBox label="En hausse" value={mgr.keywords_improved > 0 ? `+${nf(mgr.keywords_improved)}` : '0'} accent="text-green-600" />
                <StatBox label="En baisse" value={mgr.keywords_declined > 0 ? `-${nf(mgr.keywords_declined)}` : '0'} accent="text-red-500" />
              </div>

              {priorityKws.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-neutral-700 mb-2">Mots-clés prioritaires</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-neutral-200 bg-[#002FA7]/5 text-left">
                          <th className="py-2 px-3 text-xs font-mono uppercase text-neutral-500">Mot-clé</th>
                          <th className="py-2 px-3 text-right text-xs font-mono uppercase text-neutral-500">Position</th>
                          <th className="py-2 px-3 text-right text-xs font-mono uppercase text-neutral-500">Évolution</th>
                        </tr>
                      </thead>
                      <tbody>
                        {priorityKws.map((k, i) => (
                          <tr key={i} className="border-b border-neutral-100 last:border-0">
                            <td className="py-2 px-3 font-medium text-neutral-900">{k.keyword}</td>
                            <td className="py-2 px-3 text-right font-mono text-lg font-bold">{k.current_position ?? '—'}</td>
                            <td className="py-2 px-3 text-right font-mono"><ChangeBadge change={k.change} /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <h3 className="text-sm font-semibold text-neutral-700 mb-2">Tous les mots-clés</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-neutral-200 bg-neutral-50 text-left">
                      <th className="py-2 px-3 text-xs font-mono uppercase text-neutral-500">Mot-clé</th>
                      {monthLabels.map((m, i) => (
                        <th key={i} className="py-2 px-3 text-right text-xs font-mono uppercase text-neutral-500">{m}</th>
                      ))}
                      <th className="py-2 px-3 text-right text-xs font-mono uppercase text-neutral-500">Actuelle</th>
                      <th className="py-2 px-3 text-right text-xs font-mono uppercase text-neutral-500">Évol.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {evoKws.map((k, i) => (
                      <tr key={i} className="border-b border-neutral-100 last:border-0">
                        <td className="py-2 px-3 font-medium text-neutral-900">
                          {k.is_priority && <span className="mr-1.5 text-amber-500">★</span>}{k.keyword}
                        </td>
                        {(k.monthly_positions || []).map((p, idx) => (
                          <td key={idx} className="py-2 px-3 text-right font-mono text-neutral-500">{p ?? '—'}</td>
                        ))}
                        <td className="py-2 px-3 text-right font-mono font-bold text-neutral-900">{k.current_position ?? '—'}</td>
                        <td className="py-2 px-3 text-right font-mono"><ChangeBadge change={k.change} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Section>
          </>
        )}

        {/* Other reports (switch) */}
        {data?.recent_reports?.length > 1 && (
          <div className="bg-white border border-neutral-200 rounded-lg no-print" data-testid="other-reports">
            <div className="p-4 border-b border-neutral-200">
              <h2 className="font-heading font-semibold text-lg text-neutral-900">Autres rapports</h2>
            </div>
            <div className="divide-y divide-neutral-100">
              {data.recent_reports.map((r) => (
                <button key={r.id} onClick={() => loadReport(r.id)}
                  className={`w-full p-4 flex items-center justify-between text-left hover:bg-neutral-50 ${r.id === selectedId ? 'bg-[#002FA7]/5' : ''}`}
                  data-testid={`report-item-${r.id}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-neutral-100 rounded flex items-center justify-center">
                      <FileText className="w-5 h-5 text-neutral-500" />
                    </div>
                    <div>
                      <p className="font-medium text-neutral-900">{r.title}</p>
                      <p className="text-sm text-neutral-500">{r.report_type}</p>
                    </div>
                  </div>
                  {r.id === selectedId
                    ? <span className="text-xs font-mono text-[#002FA7]">Affiché</span>
                    : <span className="text-xs font-mono text-neutral-400">Voir</span>}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="mt-8 text-center text-sm text-neutral-400 flex items-center justify-center gap-1">
          <Globe className="w-3.5 h-3.5" /> Rapport généré par la plateforme SEO
        </div>
      </main>
    </div>
  );
}
